/** @format */

// File: src/components/athletes/AthleteTable.jsx
// Description: Presentation layer displaying matching athlete entry badges with dynamic dictionary lookups.
// Version: 3.3.1

import { memo } from "@wordpress/element";

const AthleteTable = ({ team, entries, isDeadlinePassed, onSelectAthlete, meetingSchedule = [] }) => {
    const strictNormalize = (str) => (str ? String(str).trim().toLowerCase() : "");

    const normalizeGender = (genderStr) => {
        const clean = strictNormalize(genderStr);
        if(["m", "male", "men", "boy", "boys"].includes(clean)) return "m";
        if(["f", "female", "women", "lady", "ladies", "girl", "girls"].includes(clean)) return "f";
        return clean;
    };

    const getEntryCount = (athleteId) => {
        return entries.filter((e) => Number(e.athleteId) === Number(athleteId)).length;
    };

    const getAthleteActiveEvents = (athleteId) => {
        return entries.filter((e) => Number(e.athleteId) === Number(athleteId));
    };

    // Construct a silent look-up mapping dictionary using the passed master tracking schedule array
    const disciplineLookupMap = {};
    if(Array.isArray(meetingSchedule)) {
        meetingSchedule.forEach(item => {
            if(item.disciplineId && item.disciplineName) {
                disciplineLookupMap[strictNormalize(item.disciplineId)] = item.disciplineName;
            }
        });
    }

    return (
        <div className="table-responsive bg-white border rounded shadow-sm p-3">
            <table className="table table-hover align-middle mb-0 small">
                <thead className="table-light text-secondary small text-uppercase">
                    <tr>
                        <th className="ps-3">Athlete</th>
                        <th className="text-center" style={ { width: "110px" } }>Sex</th>
                        <th>Registered Events</th>
                        <th className="text-center" style={ { width: "80px" } }>Count</th>
                        <th className="text-end pe-3" style={ { width: "160px" } }>Actions</th>
                    </tr>
                </thead>
                <tbody className="small">
                    { team.map((athlete) => {
                        const count = getEntryCount(athlete.id);
                        const activeAthleteEvents = getAthleteActiveEvents(athlete.id);
                        const classArray = athlete.ageClass ? String(athlete.ageClass).split(",") : [];

                        let countBadgeClass = "bg-primary bg-opacity-10 text-primary border-primary border-opacity-25";
                        if(count === 0) countBadgeClass = "bg-secondary bg-opacity-10 text-secondary border-secondary border-opacity-25 opacity-50";
                        else if(count > 2) countBadgeClass = "bg-danger bg-opacity-10 text-danger border-danger border-opacity-25";

                        return (
                            <tr key={ `athlete-row-${ athlete.id }` }>
                                <td className="ps-3">
                                    <div className="athlete-fullname">
                                        <span className="fw-bold text-dark text-uppercase">{ athlete.lastName }</span>, <span className="text-secondary fw-medium">{ athlete.firstName }</span>
                                    </div>
                                    <div className="d-flex flex-wrap gap-1 mt-1 lh-base">
                                        { classArray.length > 0 ? (
                                            classArray.map((cls, i) => (
                                                <span key={ `athlete-${ athlete.id }-class-${ i }` } className="text-uppercase fw-semibold bg-light px-2 py-0.5 border rounded text-muted" style={ { fontSize: "10px", letterSpacing: "0.3px" } }>
                                                    { cls.trim() }
                                                </span>
                                            ))
                                        ) : (
                                            <span className="text-muted small fst-italic">No Class Assigned</span>
                                        ) }
                                    </div>
                                </td>
                                <td className="text-center">
                                    { (() => {
                                        const rawGender = normalizeGender(athlete.sexCode);

                                        let badgeClass = "bg-secondary bg-opacity-10 text-secondary";
                                        let displayText = athlete.sexCode || "Mixed";

                                        if(rawGender === "m") {
                                            badgeClass = "bg-primary bg-opacity-10 text-primary";
                                            displayText = "Male";
                                        } else if(rawGender === "f") {
                                            badgeClass = "bg-danger bg-opacity-10 text-danger";
                                            displayText = "Female";
                                        }

                                        return (
                                            <span className={ `badge px-2 py-1 lh-sm fw-semibold ${ badgeClass }` } style={ { fontSize: "10px" } }>
                                                { displayText }
                                            </span>
                                        );
                                    })() }
                                </td>
                                <td>
                                    { activeAthleteEvents.length > 0 ? (
                                        <div className="d-flex flex-wrap gap-1">
                                            { activeAthleteEvents.map((entry, idx) => {
                                                // Resolve matching names by querying entry string keys inside the lookups dictionary
                                                const normalizedKey = strictNormalize(entry.disciplineId);
                                                const resolvedDisciplineName = disciplineLookupMap[normalizedKey] || entry.displayName || entry.disciplineName || entry.event || entry.disciplineId;

                                                return (
                                                    <span key={ `athlete-${ athlete.id }-entry-${ idx }` } className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-20 text-uppercase lh-sm fw-semibold" style={ { fontSize: "10px" } }>
                                                        { resolvedDisciplineName }
                                                    </span>
                                                );
                                            }) }
                                        </div>
                                    ) : (
                                        <span className="text-muted small fst-italic">No Registered Events</span>
                                    ) }
                                </td>
                                <td className="text-center">
                                    <span className={ `badge border lh-sm fw-bold ${ countBadgeClass }` } style={ { fontSize: "11px", minWidth: "24px" } }>
                                        { count }
                                    </span>
                                </td>
                                <td className="text-end pe-3">
                                    <button
                                        className={ `btn btn-sm ${ isDeadlinePassed ? "btn-outline-secondary" : "btn-primary" } fw-bold px-3 rounded-2 shadow-sm d-inline-flex align-items-center gap-1` }
                                        onClick={ () => onSelectAthlete(athlete) }
                                        style={ { fontSize: "11px" } }
                                    >
                                        <i className={ isDeadlinePassed ? "fas fa-lock small" : "fas fa-plus-circle small" }></i>
                                        { isDeadlinePassed ? "View Entries" : "Select Events" }
                                    </button>
                                </td>
                            </tr>
                        );
                    }) }
                </tbody>
            </table>
        </div>
    );
};

export default memo(AthleteTable);